import { useEffect } from 'react';
import {
  PushNotificationGroup,
  PushNotificationSetting,
  SettingsItems,
  ThemePreference,
  TimeZone,
} from '@connect-x-app/shared-domain-models';
import {
  getPushNotificationGroupKeys,
  getPushNotificationKeysToUpdate,
  sortPushNotificationSettings,
} from '@connect-x-app/shared-utils';

import { useSettingsStore } from '../stores';
import { useGetSettings, useUpdateSettings } from './queries';

export const useSettings = () => {
  const { setSettings, setThemePreference: setThemePreferenceStore, settings } = useSettingsStore();

  const { data, isError, isFetching, refetch } = useGetSettings();

  useEffect(() => {
    if (data) {
      setSettings(data);
    }
  }, [data, setSettings]);

  const { isPending: isUpdating, mutate: updateSettings } = useUpdateSettings({
    onSettled: () => refetch(),
  });

  // Shows the change at once. A failed save puts back what the server still has (the time zone and the push settings,
  // not the theme, which only lives on the device), so the screen never shows a value that wasn't saved.
  const saveSettings = (updatedSettings: SettingsItems) => {
    const { preferences, pushNotifications } = settings;

    setSettings(updatedSettings);
    updateSettings(updatedSettings, {
      onError: () => setSettings({ preferences: { timeZone: preferences.timeZone }, pushNotifications }),
    });
  };

  const writePushNotificationKeys = (keys: PushNotificationSetting['key'][], isEnabled: boolean) => {
    if (!settings.pushNotifications.some(setting => keys.includes(setting.key))) {
      return;
    }

    const updatedPushNotifications = settings.pushNotifications.map(setting =>
      keys.includes(setting.key) ? { ...setting, isEnabled, isUpdating: true } : setting,
    );

    saveSettings({ ...settings, pushNotifications: sortPushNotificationSettings(updatedPushNotifications) });
  };

  const onUpdatePushNotificationSetting = (key: PushNotificationSetting['key'], isEnabled: boolean) => {
    writePushNotificationKeys(getPushNotificationKeysToUpdate(key, isEnabled), isEnabled);
  };

  const onToggleAllPushNotifications = (group: PushNotificationGroup, isEnabled: boolean) => {
    writePushNotificationKeys(getPushNotificationGroupKeys(group), isEnabled);
  };

  const setThemePreference = (themePreference: ThemePreference) => {
    setThemePreferenceStore(themePreference);
  };

  const updateTimeZoneSetting = (timeZone: TimeZone) => {
    saveSettings({ ...settings, preferences: { ...settings.preferences, timeZone } });
  };

  return {
    // Only when the settings never loaded: a failed refetch keeps the settings the screen already shows.
    isError: isError && !data,
    isLoading: !settings && isFetching,
    isUpdating,
    onToggleAllPushNotifications,
    onUpdatePushNotificationSetting,
    refetch,
    setThemePreference,
    settings,
    updateTimeZoneSetting,
  };
};
